import { useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'dibs-react-intl';
import styles from '../../styles/main.scss';

// components
import Text from '../inputs/Text';

const ZIP_CODE_DASH_POSITION = 6;

//remove all non-word (non-alphanumeric) characters, example: - / , . ~ § ±
const unformatZipCode = str => str.replace(/\W/g, '');

// remove all non-ascii symbols from the zipCode
const normalizeZipCode = str => str.replace(/[^\x00-\x7F]/g, '');

const formatZipCode = str => {
    const unformattedZipCode = unformatZipCode(str);
    return unformattedZipCode.length > 5
        ? `${unformattedZipCode.slice(0, 5)}-${unformattedZipCode.slice(5)}`
        : unformattedZipCode;
};

const messages = defineMessages({
    placeholder: {
        id: 'dc.AddressForm.ZipCode.placeholder',
        defaultMessage: 'Zip Code*',
    },
});

const ZipCode = ({
    value,
    autoFocus = false,
    fieldSize,
    isCountryUS = false,
    isBlurred,
    disabled,
    disableAutocomplete,
    error,
    onChange,
    onBlur = () => {},
    wrapperClass,
}) => {
    const intl = useIntl();
    const cursorPositionRef = useRef(value.length);
    const inputNodeRef = useRef(null);
    const prevValue = useRef('');

    const handleChange = useCallback(
        ({ name, value: _value, event }) => {
            const newValue = isCountryUS ? unformatZipCode(_value).trim() : _value.trim();
            const hasDeletedDigit = newValue.length < prevValue.current.length;
            const hasAddedDigit = newValue.length > prevValue.current.length;
            const hasValueLengthChanged = hasAddedDigit || hasDeletedDigit;
            const isCursorNextToDash = event.target.selectionStart === ZIP_CODE_DASH_POSITION;
            let cursorOffset = 0;

            if (isCountryUS && isCursorNextToDash) {
                if (hasAddedDigit || !hasValueLengthChanged) {
                    cursorOffset = 1;
                } else if (hasDeletedDigit) {
                    cursorOffset = -1;
                }
            }

            cursorPositionRef.current = event.target.selectionStart + cursorOffset;
            inputNodeRef.current = event.target;
            prevValue.current = newValue;
            onChange({ name, value: normalizeZipCode(newValue) });
        },
        [isCountryUS, onChange]
    );

    useEffect(() => {
        if (inputNodeRef.current) {
            inputNodeRef.current.setSelectionRange(
                cursorPositionRef.current,
                cursorPositionRef.current
            );
        }
    }, [value]);

    return (
        <Text
            name="zipCode"
            dataTn="zip-code"
            value={isCountryUS ? formatZipCode(value) : value}
            wrapperClass={wrapperClass || styles.left}
            placeholder={intl.formatMessage(messages.placeholder)}
            autoComplete="postal-code"
            maxLength={isCountryUS ? 10 : 9}
            autoFocus={autoFocus}
            fieldSize={fieldSize}
            pattern={isCountryUS ? '\\d*' : '.*'}
            isBlurred={isBlurred}
            disabled={disabled}
            disableAutocomplete={disableAutocomplete}
            error={error}
            onChange={handleChange}
            onBlur={({ name, value: val }) =>
                onBlur({ name, value: isCountryUS ? unformatZipCode(val) : val })
            }
        />
    );
};

ZipCode.propTypes = {
    value: PropTypes.string.isRequired,
    error: PropTypes.string,
    autoFocus: PropTypes.bool,
    isCountryUS: PropTypes.bool,
    fieldSize: PropTypes.oneOf(['small', 'medium', 'large']),
    isBlurred: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    disableAutocomplete: PropTypes.bool,
    wrapperClass: PropTypes.string,
};

export default ZipCode;
